import { DAW_FILE_EXTENSIONS, FileSyncStatus } from '@/common/models/enums';
import { STEMS_AUDIO_EXTENSIONS } from '@/common/utils/projectDetection';
import { shouldIgnoreFile } from '@/common/utils/fileUtils';
import type { GetProjectLibraryFilesQueryData } from '@/generated/graphql';

export type LibraryFilter = 'all' | 'daw' | 'stem' | 'outdated';
export type ProjectKind = 'daw' | 'stem' | 'unknown';
export type ProjectSyncState = 'outdated' | 'synced' | 'unverified';
export type LibraryFile =
  GetProjectLibraryFilesQueryData['projectVersion']['files'][number];
export interface LibraryProject {
  _id: string;
  versionHistory?: Array<{ version: number }> | null;
}
export interface LibraryProjectStatus {
  kind: ProjectKind;
  sync: ProjectSyncState;
}

/** DAW files take precedence over audio assets within a DAW session. */
export function classifyProject(
  files: ReadonlyArray<{ fileName: string }>
): ProjectKind {
  const paths = files
    .filter((file) => !shouldIgnoreFile(file.fileName))
    .map((file) => file.fileName.toLowerCase().replace(/\\/g, '/'));
  if (
    paths.some((path) =>
      path
        .split('/')
        .some((part) =>
          DAW_FILE_EXTENSIONS.some((extension) => part.endsWith(extension))
        )
    )
  ) {
    return 'daw';
  }
  if (
    paths.some((path) =>
      STEMS_AUDIO_EXTENSIONS.has(path.slice(path.lastIndexOf('.')))
    )
  ) {
    return 'stem';
  }
  return 'unknown';
}

export function summarizeSync(
  statuses: ReadonlyArray<{ status: string }>
): ProjectSyncState {
  const differences: string[] = [
    FileSyncStatus.BEHIND,
    FileSyncStatus.NEWER,
    FileSyncStatus.NEW,
    FileSyncStatus.DELETED,
  ];
  if (statuses.some(({ status }) => differences.includes(status)))
    return 'outdated';
  if (statuses.some(({ status }) => status !== FileSyncStatus.UP_TO_DATE))
    return 'unverified';
  return 'synced';
}

export function matchesLibraryFilter(
  filter: LibraryFilter,
  status?: LibraryProjectStatus
): boolean {
  if (filter === 'all') return true;
  if (filter === 'outdated') return status?.sync === 'outdated';
  return status?.kind === filter;
}

interface ScanOptions {
  projects: readonly LibraryProject[];
  loadFiles: (projectId: string, version: number) => Promise<LibraryFile[]>;
  getPath: (projectId: string) => string | null;
  checkFiles?: (
    projectId: string,
    path: string,
    files: LibraryFile[]
  ) => Promise<{
    success: boolean;
    fileStatuses?: Array<{ status: string }>;
  }>;
  isCancelled?: () => boolean;
}

/** Limit disk hashing and manifest requests to two projects at a time. */
export async function scanProjectLibrary({
  projects,
  loadFiles,
  getPath,
  checkFiles,
  isCancelled = () => false,
}: ScanOptions): Promise<Record<string, LibraryProjectStatus>> {
  const results: Record<string, LibraryProjectStatus> = {};
  let next = 0;
  async function worker() {
    while (!isCancelled() && next < projects.length) {
      const project = projects[next++];
      const result: LibraryProjectStatus = {
        kind: 'unknown',
        sync: 'unverified',
      };
      try {
        const version = Math.max(
          0,
          ...(project.versionHistory ?? []).map((item) => item.version)
        );
        const files = version > 0 ? await loadFiles(project._id, version) : [];
        if (isCancelled()) return;
        const relevantFiles = files.filter(
          (file) => !shouldIgnoreFile(file.fileName)
        );
        result.kind = classifyProject(relevantFiles);
        const path = getPath(project._id);
        if (checkFiles && path) {
          const check = await checkFiles(project._id, path, relevantFiles);
          if (check.success && check.fileStatuses) {
            result.sync = summarizeSync(check.fileStatuses);
            if (
              result.sync === 'synced' &&
              check.fileStatuses.length < relevantFiles.length
            ) {
              result.sync = 'unverified';
            }
          }
        }
      } catch {
        // Unavailable manifests, unreadable folders and failed checks are not
        // evidence that a project is synced (or out of date).
      }
      if (!isCancelled()) results[project._id] = result;
    }
  }
  await Promise.all([worker(), worker()]);
  return results;
}
